import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";

import SidebarStudent from "./Utils/SidebarStudent";
import CreateApplicant, { ApplicantFormRef } from "./Resource/CreateApplicant";
import CreateCurrentaddress, { CurrentaddressFormRef } from "./Resource/CreateCurrentaddress";
import CreatePermaddress, { PermaddressFormRef } from "./Resource/CreatePermaddress";

import "./Dashboard.css";
import "./PersonalDetails.css";
import { logout } from "../apis/backend";
import { toast } from "../utils/toast";

import { useStudentProgress } from "../context/StudentProgressContext";
import LogoutButton from "./LogoutButton";

export default function PersonalDetails() {
  const navigate = useNavigate();
  const { refreshProgress } = useStudentProgress(); // ✅ Get refresh function

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sameAsCurrentAddress, setSameAsCurrentAddress] = useState(false);

  const [dataToSave, setDataToSave] = useState<any>({});

  // Refs for child form components
  const applicantRef = useRef<ApplicantFormRef>(null);
  const currentAddressRef = useRef<CurrentaddressFormRef>(null);
  const permAddressRef = useRef<PermaddressFormRef>(null);

  const handleSaveAndNext = async () => {
    try {
      setIsSaving(true);

      let allErrors: Record<string, string> = {};

      // Save + collect errors
      if (applicantRef.current) {
        const applicantErrors = await applicantRef.current.save();
        Object.assign(allErrors, applicantErrors);
      }

      if (currentAddressRef.current) {
        const currentAddrErrors = await currentAddressRef.current.save();
        Object.assign(allErrors, currentAddrErrors);
      }

      if (permAddressRef.current) {
        const permAddrErrors = await permAddressRef.current.save();
        Object.assign(allErrors, permAddrErrors);
      }

      // Trigger progress update
      await refreshProgress();

      //  SINGLE TOAST LOGIC
      if (Object.keys(allErrors).length > 0) {
        toast.error("Please complete all required fields.");
      } else {
        toast.success("All details saved successfully!");
        setTimeout(() => navigate("/parentdetails"), 1000);
      }

    } catch (error) {
      console.error("Error saving details:", error);
      toast.error("Error saving details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="dash-layout">
      {/* Sidebar */}
      <SidebarStudent
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((p) => !p)}
      />

      {/* Main Wrapper */}
      <div
        className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""
          }`}
      >
        {/* Header */}
        <header className="dash-header">
          <h1 className="dash-page-title">Personal Details</h1>

          <div className="dash-user-profile">
            <div
              className="dash-profile-circle"
              onClick={() => setShowDropdown((p) => !p)}
            >
              <span>S</span>
            </div>

            {showDropdown && (
              <div className="dash-profile-dropdown">
                <LogoutButton />
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="dash-content-body">
          {/* Instructions Box */}
          {/* <div className="instructions-box">
            <div className="instructions-title">Instruction/Guidelines</div>
            <div className="instructions-text">
              • Please fill up the personal details. Uploading of profile photo is must, should be less than 2 MB
            </div>
          </div> */}

          {/* Personal Details Form */}
          <CreateApplicant
            ref={applicantRef}
            dataToSave={dataToSave}
            setDataToSave={setDataToSave}
          />

          {/* Current Address Form */}
          <CreateCurrentaddress
            ref={currentAddressRef}
            dataToSave={dataToSave}
            setDataToSave={setDataToSave}
          />

          {/* Same as Current Address Checkbox */}
          <div className="same-address-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={sameAsCurrentAddress}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setSameAsCurrentAddress(isChecked);

                  if (isChecked && currentAddressRef.current && permAddressRef.current) {
                    const currentData = currentAddressRef.current.getData();
                    permAddressRef.current.setDataFromCurrentAddress(currentData);
                  } else if (!isChecked && permAddressRef.current) {
                    // Clear permanent address fields when unchecked
                    permAddressRef.current.setDataFromCurrentAddress({
                      line1: "",
                      line2: "",
                      city: "",
                      state: "",
                      country: "",
                      pincode: "",
                    });
                  }
                }}
              />
              <span>Permanent address is same as current address</span>
            </label>
          </div>

          {/* Permanent Address Form */}
          <CreatePermaddress
            dataToSave={dataToSave}
            setDataToSave={setDataToSave}
            ref={permAddressRef} />

          {/* Save & Next Buttons */}
          <div className="form-actions gap-2">
            <button
              className="save-btn"
              type="button"
              onClick={handleSaveAndNext}
              disabled={isSaving}
              style={{ background: "#28a745" }}
            >
              {isSaving ? "Saving..." : "Save & Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
